import { Subject } from "rxjs";
import { takeUntil, distinctUntilChanged } from "rxjs/operators";
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  AfterViewInit,
  QueryList,
  SimpleChanges,
  ContentChildren,
  ChangeDetectorRef,
  OnChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  OnDestroy,
  AfterContentInit,
} from "@angular/core";
import { animate, style, transition, trigger, state } from '@angular/animations';
import { PhxTabComponent } from "./phx-tab.component";
import { PhxTabsetType } from "./phx-tabset.type";
import { TabsetService } from './phx-tabset.service';

@Component({
  selector: 'phx-tabset',
  templateUrl: './phx-tabset.component.html',
  styleUrls: ['./phx-tabset.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TabsetService],
  host: {
    class: 'phx-tabset',
    '[class.phx-tabset-primary]': 'phxTabType === "primary"',
    '[class.phx-tabset-secondary]': 'phxTabType === "secondary"',
  },
  animations: [
    trigger('slideAnimation', [
      state('left', style({ transform: 'translateX(0)' })),
      state('right', style({ transform: 'translateX(100%)' })),
      transition('* => *', animate(300))
    ])
  ]
})
export class PhxTabsetComponent implements AfterViewInit, AfterContentInit, OnChanges, OnDestroy {
  @Input() public phxActiveTabIndex: number = 0;
  @Input() public phxTabType: PhxTabsetType = 'secondary';
  @Input() public phxShouldMatchExactLink: boolean = false;
  @Input() public phxMatchUrl: boolean = false;
  @Output() public phxActiveTabIndexChange: EventEmitter<number> = new EventEmitter<number>();
  @ContentChildren(PhxTabComponent) public tabs: QueryList<PhxTabComponent> = new QueryList<PhxTabComponent>();
  public indicatorTransformX: string = '0px';
  public indicatorWidth: string = '0px';
  public showIndicator: boolean = false;
  private $destroy: Subject<void> = new Subject<void>();

  constructor(private _tabsetService: TabsetService, private _cdr: ChangeDetectorRef) { }

  ngAfterContentInit(): void {
    this._tabsetService.changeIndicatorPosition$.pipe(takeUntil(this.$destroy)).subscribe(() => {
      this.checkAndUpdateIndicator();
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { phxActiveTabIndex, phxTabType, phxShouldMatchExactLink, phxMatchUrl } = changes;
    if (phxActiveTabIndex && this.tabs && this.tabs.length > 0 && !this.phxMatchUrl) {
      if(phxActiveTabIndex.currentValue >= this.tabs.length || phxActiveTabIndex.currentValue < 0) {
        this.hideIndicator();
        return;
      }
      this._tabsetService.onTabChanged(this.tabs.toArray()[phxActiveTabIndex.currentValue]);
    } else if (phxActiveTabIndex && phxActiveTabIndex.currentValue === -1) {
      // Explicitly hide indicator when activeTabIndex is -1
      this.hideIndicator();
    }
    if (phxTabType) {
      this._tabsetService.onTabTypeChanged(phxTabType.currentValue);
    }
    if (phxShouldMatchExactLink) {
      this._tabsetService.phxShouldMatchExactLink = phxShouldMatchExactLink.currentValue;
    }
    if (phxMatchUrl) {
      this._tabsetService.phxMatchUrl = phxMatchUrl.currentValue;
    }
  }

  public ngAfterViewInit() {
    if (!this.phxMatchUrl) {
      if (this.phxActiveTabIndex >= 0 && this.phxActiveTabIndex < this.tabs.length) {
        const activeTab = this.tabs.toArray()[this.phxActiveTabIndex];
        this._tabsetService.onTabChanged(activeTab);
      } else {
        this.hideIndicator();
      }
    }
    this._tabsetService.tabChanged$.pipe(
      takeUntil(this.$destroy),
      distinctUntilChanged((prev, curr) => prev?.tabBox?.nativeElement.offsetWidth == curr?.tabBox?.nativeElement.offsetWidth
        && prev?.tabBox?.nativeElement.offsetLeft == curr?.tabBox?.nativeElement.offsetLeft),
    ).subscribe(currentTab => {
      if (currentTab) {
        let index = this.tabs.toArray().indexOf(currentTab);
        if(index >= 0 && this.phxActiveTabIndex != index) {
          this.phxActiveTabIndexChange.emit(index);
          this.phxActiveTabIndex = index;
        }
        this.updateIndicatorPosition(currentTab?.tabBox);
      }
      else {
        // No active tab, emit -1 to indicate no active tab
        if (this.phxActiveTabIndex !== -1) {
          this.phxActiveTabIndexChange.emit(-1);
          this.phxActiveTabIndex = -1;
        }
        this.checkAndUpdateIndicator();
      }
    });
  }

  public ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }

  private _resetIndicatorPosition(): void {
    this.indicatorWidth = '0px';
    this.indicatorTransformX = 'translateX(0px)';
  }

  private hideIndicator(): void {
    this.indicatorWidth = '0px';
    this.indicatorTransformX = 'translateX(0px)';
    this.showIndicator = false;
    this._cdr.detectChanges();
  }

  private checkAndUpdateIndicator(): void {
    const activeTab: PhxTabComponent = this.tabs.find(tab => tab.isActive);
    if (activeTab) {
      this.updateIndicatorPosition(activeTab.tabBox);
    } else {
      this.hideIndicator();
    }
  }

  private updateIndicatorPosition(tabBox: ElementRef): void {
    const activeTabElement = tabBox?.nativeElement;
    if (activeTabElement) {
      // rAF is required to let the tabs render before adjusting
      // the indicator position. Otherwise the indicator position
      // is set incorrectly after ivy was enabled
      requestAnimationFrame(() => {
        this.indicatorWidth = `${activeTabElement.offsetWidth}px`;
        this.indicatorTransformX = `translateX(${activeTabElement.offsetLeft}px)`;
        this.showIndicator = true;
        this._cdr.detectChanges();
      });
    }
  }
}
